import { PartSearchResultDetails } from '@/entities/PartSearchResultDetails';
import { LoadingState } from '@/entities/LoadingState';
import { MutationTree, ActionTree, GetterTree, Module, ActionContext } from 'vuex';
import { PartSearchService } from '@/services/part-search-service';
import { RootState } from '../rootState';
import removeNamespace from '../removeNamespace';
import { PartService } from '@/services/part-service';
import { PartAvailability } from '@/entities/PartAvailability';
import { QuickSearchResult } from '@/entities/quick-search/QuickSearchResult';
import { VinSearchResult } from '@/entities/vin-search/VinSearchResult';

const quickSearchService = new PartSearchService();
const partService = new PartService();

const namespace = 'quickSearch';

export const QuickSearchModuleTypes = {
    getters: {
        searchTerm: `${namespace}/searchTerm`,
        partSearchResults: `${namespace}/partSearchResults`,
        totalNumberOfParts: `${namespace}/totalNumberOfParts`,
        searchResultsLoadingState: `${namespace}/searchResultsLoadingState`,
        isVinSearchResult: `${namespace}/isVinSearchResult`,
        vinSearchResult: `${namespace}/vinSearchResult`,
    },
    mutations: {
        loadSearchResults: `${namespace}/loadSearchResults`,
        loadSearchResultsSuccess: `${namespace}/loadSearchResultsSuccess`,
        loadSearchResultsFail: `${namespace}/loadSearchResultsFail`,
        closeQuickSearchResults: `${namespace}/closeQuickSearchResults`,
        updatePartAvailability: `${namespace}/updatePartAvailability`,
        updatePartAvailabilityLoadingState: `${namespace}/updatePartAvailabilityLoadingState`,
    },
    actions: {
        loadSearchResults: `${namespace}/loadSearchResults`,
        closeQuickSearchResults: `${namespace}/closeQuickSearchResults`,
        reloadPartAvailabilityForPart: `${namespace}/reloadPartAvailabilityForPart`,
    },
};

const types = removeNamespace(namespace, QuickSearchModuleTypes);

export interface QuickSearchState {
    searchTerm: string | null;
    searchResults: PartSearchResultDetails[];
    totalNumberOfResults: number | null;
    searchResultLoadingState: LoadingState;
    isVinSearchResult: boolean;
    vinSearchResult: VinSearchResult | null;
}

const state: QuickSearchState = {
    searchTerm: null,
    searchResults: [],
    totalNumberOfResults: null,
    searchResultLoadingState: LoadingState.Indeterminate,
    isVinSearchResult: false,
    vinSearchResult: null,
};

const mutations: MutationTree<QuickSearchState> = {
    [types.mutations.loadSearchResults](currentState: QuickSearchState, payload: string): void {
        currentState.searchResultLoadingState = LoadingState.Loading;
        currentState.searchTerm = payload;
    },
    [types.mutations.loadSearchResultsSuccess](currentState: QuickSearchState, payload: QuickSearchResult): void {
        currentState.isVinSearchResult = false;
        const results = payload.partSearchResultPage!.partSearchResultDetails.filter((result) => result != null);
        results.forEach((result) => (result.isPartAvailabilityLoading = false));
        currentState.searchResults = results;
        currentState.vinSearchResult = null;
        currentState.totalNumberOfResults = payload.partSearchResultPage!.totalNumberOfResults;

        currentState.searchResultLoadingState = LoadingState.Loaded;
    },
    [types.mutations.loadSearchResultsFail](currentState: QuickSearchState): void {
        currentState.totalNumberOfResults = null;
        currentState.searchTerm = null;
        currentState.searchResults = [];
        currentState.vinSearchResult = null;
        currentState.searchResultLoadingState = LoadingState.Failed;
    },
    [types.mutations.closeQuickSearchResults](currentState: QuickSearchState): void {
        currentState.searchTerm = null;
        currentState.totalNumberOfResults = null;
        currentState.searchResults = [];
        currentState.vinSearchResult = null;
        currentState.searchResultLoadingState = LoadingState.Indeterminate;
    },
    [types.mutations.updatePartAvailability](
        currentState: QuickSearchState,
        payload: { partId: string; partAvailability: PartAvailability }
    ): void {
        const foundPart = currentState.searchResults.find((result) => result.searchResult.id === payload.partId);
        if (foundPart) {
            foundPart.partAvailability = payload.partAvailability;
        }
    },
    [types.mutations.updatePartAvailabilityLoadingState](
        currentState: QuickSearchState,
        payload: { partId: string; isLoading: boolean }
    ): void {
        const foundPart = currentState.searchResults.find((result) => result.searchResult.id === payload.partId);
        if (foundPart) {
            foundPart.isPartAvailabilityLoading = payload.isLoading;
        }
    },
};

const actions: ActionTree<QuickSearchState, RootState> = {
    [types.actions.loadSearchResults](
        context: ActionContext<QuickSearchState, RootState>,
        payload: { searchTerm: string }
    ): void {
        context.commit(types.mutations.loadSearchResults, payload.searchTerm);
        quickSearchService
            .getQuickSearchResults(payload.searchTerm)
            .then((response) => {
                context.commit(types.mutations.loadSearchResultsSuccess, response);
            })
            .catch((e) => {
                context.commit(types.mutations.loadSearchResultsFail);
            });
    },
    [types.actions.closeQuickSearchResults](context: ActionContext<QuickSearchState, RootState>): void {
        context.commit(types.mutations.closeQuickSearchResults);
    },
    async [types.actions.reloadPartAvailabilityForPart](
        context: ActionContext<QuickSearchState, RootState>,
        payload: { partId: string; quantity: number }
    ): Promise<void> {
        context.commit(types.mutations.updatePartAvailabilityLoadingState, { partId: payload.partId, isLoading: true });
        const partAvailability = await partService.getAvailabilityOfPart(payload.partId, payload.quantity);
        if (partAvailability) {
            context.commit(types.mutations.updatePartAvailability, {
                partId: payload.partId,
                partAvailability,
            });
        }
        context.commit(types.mutations.updatePartAvailabilityLoadingState, {
            partId: payload.partId,
            isLoading: false,
        });
    },
};

const getters: GetterTree<QuickSearchState, RootState> = {
    [types.getters.searchTerm]: (currentState) => {
        return currentState.searchTerm;
    },
    [types.getters.partSearchResults]: (currentState) => {
        return currentState.searchResults;
    },
    [types.getters.totalNumberOfParts]: (currentState) => {
        return currentState.totalNumberOfResults;
    },
    [types.getters.searchResultsLoadingState]: (currentState) => {
        return currentState.searchResultLoadingState;
    },
    [types.getters.isVinSearchResult]: (currentState) => {
        return currentState.isVinSearchResult;
    },
    [types.getters.vinSearchResult]: (currentState) => {
        return currentState.vinSearchResult;
    },
};

const module: Module<QuickSearchState, RootState> = {
    state,
    mutations,
    actions,
    getters,
    namespaced: true,
};

export default module;
